import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { MetricCard } from "@/components/MetricCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Droplets, ArrowDownToLine, ArrowUpFromLine, RotateCcw, Save } from "lucide-react";
import { WATER } from "@/constants/testIds";
import { toast } from "sonner";

export default function Water() {
  const { currentId, current, fetchProjects } = useProject();
  const { user } = useAuth();
  const [water, setWater] = useState(null);
  const [dailyLimit, setDailyLimit] = useState("");

  const fetch_ = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/projects/${currentId}/water`);
    setWater(data);
    setDailyLimit(String(data.daily_limit_l));
  };

  useEffect(() => {
    fetch_();
    const id = setInterval(fetch_, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  const reset = async () => {
    if (!window.confirm("Reset volume total ke nol?")) return;
    await api.post(`/projects/${currentId}/water/reset`);
    toast.success("Volume direset");
    fetch_();
  };

  const saveLimit = async () => {
    await api.patch(`/projects/${currentId}`, { daily_water_limit_l: Number(dailyLimit) });
    toast.success("Batas harian disimpan");
    await fetchProjects();
    fetch_();
  };

  if (!currentId) return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;
  if (!water) return <div className="text-muted-foreground">Memuat…</div>;

  const pct = water.daily_limit_l > 0 ? Math.min(100, (water.daily_out_l / water.daily_limit_l) * 100) : 0;
  const isAdmin = user?.role === "admin";

  return (
    <div>
      <PageHeader
        title="Manajemen Air"
        overline={current?.name}
        description="Perhitungan debit masuk, keluar, dan volume tersedia dengan pembatasan harian."
        actions={
          isAdmin && (
            <Button variant="outline" onClick={reset} data-testid={WATER.resetButton}>
              <RotateCcw className="w-4 h-4 mr-1" /> Reset Volume
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard overline="Air Tersedia" value={water.available_l.toFixed(1)} unit="L" icon={Droplets} hint="Masuk − Keluar" />
        <MetricCard overline="Total Masuk" value={water.total_in_l.toFixed(1)} unit="L" icon={ArrowDownToLine} />
        <MetricCard overline="Total Keluar" value={water.total_out_l.toFixed(1)} unit="L" icon={ArrowUpFromLine} accent="accent" />
        <MetricCard overline="Keluar Hari Ini" value={water.daily_out_l.toFixed(1)} unit={`/ ${water.daily_limit_l} L`} icon={Droplets} accent="accent" />
      </div>

      <div className="border border-border rounded-lg bg-card p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline text-muted-foreground">Batas Harian Keluar</div>
            <h3 className="font-display text-lg font-semibold mt-1">{water.daily_out_l.toFixed(1)} / {water.daily_limit_l} L</h3>
          </div>
          <div className={`text-sm ${pct > 100 ? "text-destructive" : pct > 80 ? "text-accent" : "text-muted-foreground"}`}>{pct.toFixed(0)}%</div>
        </div>
        <Progress value={pct} className="h-3" />
        {pct >= 100 && (
          <div className="mt-3 text-sm text-destructive">Melampaui batas harian! Pertimbangkan menutup solenoid.</div>
        )}
      </div>

      {isAdmin && (
        <div className="border border-border rounded-lg bg-card p-6 max-w-md">
          <div className="overline text-muted-foreground mb-3">Pengaturan</div>
          <Label className="text-sm">Batas debit harian (Liter)</Label>
          <div className="flex gap-2 mt-2">
            <Input
              type="number"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
              data-testid={WATER.dailyLimitInput}
            />
            <Button onClick={saveLimit}>
              <Save className="w-4 h-4 mr-1" /> Simpan
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Reset terakhir: {new Date(water.last_reset).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
